import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  SemanticICONS,
  SemanticCOLORS,
  Popup,
  Icon,
  Input,
  Message,
  Form,
  Button,
} from "semantic-ui-react";
import { selectedPromptTestIds } from "../testUtils/testIdsSelectors";
import { Text } from "../../../components/Text";
import { getProductImages } from "../../../api/action";
import { ProductId } from "../../product";
import { useSelector } from "react-redux";
import { RootState } from "../../../utils/store";
import { validateImageUrl } from "../utils";
import { CopyButton } from "./Components/Buttons";

const explainImage = {
  header: "The images will be analyzed by the model to generate text from",
  content:
    "Example: A picture of blue jeans. The model will generate text based on the images.",
};

const ImagePreview: React.FC<{ url: string }> = ({ url }) => {
  const [error, setError] = React.useState(false);
  return (
    <div className="tw-p-1">
      {error ? (
        <span className="tw-text-sm tw-text-gray-500">Unable to load</span>
      ) : (
        <img
          src={url}
          alt=""
          className="tw-max-w-[120px] tw-max-h-[120px] tw-object-contain tw-block"
          onError={(): void => setError(true)}
        />
      )}
    </div>
  );
};

type Props = {
  value: string[];
  onChange: (urls: string[]) => void;
  productId?: ProductId;
  onSave?: (urls: string[]) => void;
  handleCopyImageUrl?: (value: string) => void;
  onEnabledChange?: (urls: string[]) => void;
  skipProductImage?: boolean;
};

export const ImageUrlInput: React.FC<Props> = ({
  value,
  onChange,
  productId,
  onSave,
  handleCopyImageUrl,
  onEnabledChange,
  skipProductImage,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const isMulti = onEnabledChange !== undefined;

  const [validationState, setValidationState] = useState<
    Record<string, { valid: boolean; validated: boolean; loading: boolean }>
  >({});
  const [disableInput, setDisableInput] = useState<boolean>(false);

  const idCounterRef = useRef(0);
  const itemIdsRef = useRef<string[]>([]);
  if (itemIdsRef.current.length < value.length) {
    while (itemIdsRef.current.length < value.length) {
      idCounterRef.current += 1;
      itemIdsRef.current.push(`image-url-${idCounterRef.current}`);
    }
  } else if (itemIdsRef.current.length > value.length) {
    itemIdsRef.current = itemIdsRef.current.slice(0, value.length);
  }

  const [enabledById, setEnabledById] = useState<Record<string, boolean>>({});
  const enabledByIdRef = useRef(enabledById);
  enabledByIdRef.current = enabledById;

  useEffect(() => {
    if (!isMulti || itemIdsRef.current.length === 0) return;
    setEnabledById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of itemIdsRef.current) {
        if (!(id in next)) {
          next[id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isMulti, value.length]);

  useEffect(() => {
    if (!isMulti) return;
    const enabled = value.filter(
      (_, i) => enabledByIdRef.current[itemIdsRef.current[i]] !== false
    );
    onEnabledChange(enabled);
  }, [isMulti, enabledById, value, onEnabledChange]);

  const toggleEnabled = (index: number): void => {
    const id = itemIdsRef.current[index];
    setEnabledById((prev) => ({
      ...prev,
      [id]: !(prev[id] !== false),
    }));
  };

  const allEnabled =
    itemIdsRef.current.length > 0 &&
    itemIdsRef.current.every((id) => enabledById[id] !== false);

  const toggleAllEnabled = (): void => {
    const next: Record<string, boolean> = {};
    for (const id of itemIdsRef.current) {
      next[id] = !allEnabled;
    }
    setEnabledById(next);
  };

  const removeImageUrl = (index: number): void => {
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  const getValidationKey = (index: number, url: string | undefined): string => {
    const s = (url ?? "").trim();
    return s || `empty-${index}`;
  };

  const getEnabledUrlsFromList = (urls: string[]): string[] =>
    urls.filter(
      (_, i) => enabledByIdRef.current[itemIdsRef.current[i]] !== false
    );

  const validateUrlDebounced = useCallback(
    debounce(
      async (
        urlKey: string,
        url: string,
        urlsAtValidationTime: string[]
      ): Promise<void> => {
        if (!(url ?? "").trim()) {
          setValidationState((prev) => ({
            ...prev,
            [urlKey]: { valid: true, validated: false, loading: false },
          }));
          return;
        }
        const valid = await validateImageUrl(url);
        setValidationState((prev) => ({
          ...prev,
          [urlKey]: { valid, validated: true, loading: false },
        }));
        if (valid) {
          onSave?.(urlsAtValidationTime);
        }
      },
      1000
    ),
    [onSave]
  );

  const getIconForUrl = (
    index: number,
    url: string
  ):
    | SemanticICONS
    | { name: SemanticICONS; color?: SemanticCOLORS; loading?: boolean } => {
    const key = getValidationKey(index, url);
    const state = validationState[key];
    if (!state) return undefined;
    if (state.loading) return { name: "circle notch", loading: true };
    if (!state.validated || !(url ?? "").trim()) return undefined;
    if (state.valid) return { name: "check", color: "green" };
    return "warning sign";
  };

  const updateUrl = (index: number, newValue: string): void => {
    const safeValue = newValue ?? "";
    const next = [...value];
    next[index] = safeValue;
    onChange(next);
    const key = getValidationKey(index, safeValue);
    setValidationState((prev) => ({
      ...prev,
      [key]: { valid: true, validated: false, loading: true },
    }));
    const urlsToSave = isMulti ? getEnabledUrlsFromList(next) : next;
    validateUrlDebounced(key, safeValue, urlsToSave);
  };

  useEffect(() => {
    let canceled = false;
    const hasUrls = value.some((u) => (u ?? "").trim() !== "");
    if (!token || !productId || hasUrls || skipProductImage) return;
    setDisableInput(true);
    getProductImages(token, productId)
      .then((images) => {
        if (canceled) return;
        if (images.length > 0) {
          const urls = images.map((img) => img.imageUrl ?? "");
          const firstUrl = urls[0] ?? "";
          const urlsToSet = isMulti ? urls : [firstUrl];
          onChange(urlsToSet);
          onSave?.(urlsToSet);
          const firstKey = firstUrl.trim() ? firstUrl : "empty-0";
          setValidationState((prev) => ({
            ...prev,
            [firstKey]: { valid: true, validated: false, loading: true },
          }));
          validateUrlDebounced(firstKey, firstUrl, urlsToSet);
        }
        setDisableInput(false);
      })
      .catch(() => {
        if (canceled) return;
        setDisableInput(false);
      });
    return (): void => {
      canceled = true;
    };
  }, [token, productId, skipProductImage, isMulti]);

  const hasInvalidUrl = value.some(
    (url, i) =>
      (url ?? "").trim() &&
      validationState[getValidationKey(i, url)]?.validated &&
      !validationState[getValidationKey(i, url)].valid
  );
  const hasAnyUrl = value.some((url) => (url ?? "").trim());
  const copyValue = value.filter(Boolean).join("\n");

  return (
    <Form.Field error={hasInvalidUrl && hasAnyUrl}>
      <Popup
        header={explainImage.header}
        content={explainImage.content}
        trigger={
          <label
            style={{ display: "inline-block" }}
            data-testid={selectedPromptTestIds.image.label}
          >
            <Icon name="question circle" /> Images:
          </label>
        }
        size="tiny"
        wide="very"
        hoverable
      />{" "}
      {hasAnyUrl && handleCopyImageUrl && (
        <CopyButton
          onCopy={(): void => handleCopyImageUrl(copyValue)}
          floated="right"
        />
      )}
      <div className="tw-flex tw-justify-between tw-gap-2 tw-mb-2 tw-items-center tw-px-2 tw-py-1">
        <Text color="grey" className="descriptive-helper-text" compact>
          These images will be analyzed by the model
        </Text>
        {isMulti && value.length > 0 && (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-flex-1 tw-text-sm tw-text-gray-600">
              {allEnabled ? "Deselect all" : "Select all"}
            </span>
            <label
              className="tw-flex tw-items-center tw-cursor-pointer tw-shrink-0"
              title={allEnabled ? "Deselect all images" : "Select all images"}
            >
              <input
                type="checkbox"
                checked={allEnabled}
                onChange={toggleAllEnabled}
                disabled={disableInput}
                className="tw-form-checkbox tw-h-4 tw-w-4 tw-rounded tw-border-gray-400 tw-text-blue-600 focus:tw-ring-2 focus:tw-ring-blue-500"
              />
            </label>
          </div>
        )}
      </div>
      {value.length === 0 ? (
        <Input
          data-testid={selectedPromptTestIds.image.input}
          disabled={disableInput}
          placeholder="https://example.com/blue-jeans.jpg"
          onChange={(_, { value: val }): void => {
            const v = val ?? "";
            onChange([v]);
            const key = (v || "").trim() ? (v || "").trim() : "empty-0";
            setValidationState({
              [key]: { valid: true, validated: false, loading: true },
            });
            validateUrlDebounced(key, v, [v]);
          }}
        />
      ) : (
        value.map((url, index) => {
          const itemId = itemIdsRef.current[index];
          const isEnabled = enabledById[itemId] !== false;
          return (
            <div
              key={itemId}
              className={`tw-flex tw-gap-2 tw-mb-1 tw-items-center tw-rounded tw-px-2 tw-py-1 ${
                isMulti && !isEnabled ? "tw-opacity-70" : ""
              }`}
            >
              <button
                type="button"
                onClick={(): void => removeImageUrl(index)}
                disabled={disableInput}
                className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors tw-shrink-0 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                title="Remove image URL"
              >
                <Icon name="close" />
              </button>
              {(url ?? "").trim() ? (
                <Popup
                  trigger={
                    <span className="tw-flex tw-flex-1">
                      <Input
                        data-testid={
                          index === 0
                            ? selectedPromptTestIds.image.input
                            : undefined
                        }
                        disabled={disableInput || (isMulti && !isEnabled)}
                        icon={getIconForUrl(index, url)}
                        value={url ?? ""}
                        onChange={(_, { value }): void =>
                          updateUrl(index, value ?? "")
                        }
                        placeholder="https://example.com/blue-jeans.jpg"
                        className="tw-flex-1"
                      />
                    </span>
                  }
                  position="top left"
                  hoverable
                  content={<ImagePreview url={url ?? ""} />}
                />
              ) : (
                <Input
                  data-testid={
                    index === 0 ? selectedPromptTestIds.image.input : undefined
                  }
                  disabled={disableInput || (isMulti && !isEnabled)}
                  icon={getIconForUrl(index, url)}
                  value={url ?? ""}
                  onChange={(_, { value }): void =>
                    updateUrl(index, value ?? "")
                  }
                  placeholder="https://example.com/blue-jeans.jpg"
                  className="tw-flex-1"
                />
              )}
              {isMulti && (
                <label
                  className="tw-flex tw-items-center tw-cursor-pointer tw-shrink-0"
                  title="Include in generation"
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(): void => toggleEnabled(index)}
                    disabled={disableInput}
                    data-testid={
                      index === 0
                        ? "selected-prompt-image-include-0"
                        : undefined
                    }
                    className="tw-form-checkbox tw-h-4 tw-w-4 tw-rounded tw-border-gray-400 tw-text-blue-600 focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-border-gray-200 disabled:tw-text-gray-200 disabled:tw-cursor-not-allowed"
                  />
                </label>
              )}
            </div>
          );
        })
      )}
      {isMulti && value.length > 0 && (
        <div className="tw-px-2 tw-py-1">
          <Button
            content="Add another image URL"
            basic
            size="mini"
            onClick={(): void => onChange([...value, ""])}
            type="button"
            className="tw-mt-1"
          />
        </div>
      )}
      <Message
        data-testid={selectedPromptTestIds.image.error}
        visible={hasInvalidUrl && hasAnyUrl}
        error
        size="tiny"
        header="Invalid image URL"
        content="We couldn't find an image at that URL. Validate that the URL is correct and that the image is publicly accessible."
      />
    </Form.Field>
  );
};
